import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { insights, isApiError } from '@repletoryreach/sdk-api';
import { Button, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { downloadBlob } from '@repletoryreach/sdk-utils/file';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useI18n } from '../../../i18n/I18nProvider';
import { formatCredits } from '../../../lib/credits';
import { formatCount } from '../../../lib/format';
import { useOrgTimezone } from '../../bookings/lib/data';
import { INSIGHTS_KEY, applyFilters, emptyFilters, insightErrorText, isFiltered, isRunning, type Filters } from '../lib/data';
import { NewReportModal, type ReportDraft } from '../NewReportModal';
import { RunningCard } from '../RunningCard';
import { MUTED, ReportStatus, depthLabel, periodLabel } from '../shared';
import { FilterBar } from './FilterBar';
import { Overview } from './Overview';
import { Topics } from './Topics';
import { Chatbot } from './Chatbot';
import { Stopped } from './Stopped';
import { Gaps } from './Gaps';
import { Demand } from './Demand';
import { Improvements } from './Improvements';
import { Chats } from './Chats';
import { Questions } from './Questions';
import { TABS, type Tab, type TabProps } from './tab';
import '../insights.css';

/**
 * One report (owner, 29 October 2026): the AI's findings and the counted figures in tabs, one filter row over all of
 * them, the chats behind every finding, and downloads (the whole report as a PDF, every table as an Excel workbook,
 * the chats shown as a CSV). A report still on its way shows its progress; one that failed says why and offers Run
 * again.
 */
export default function InsightReportPage() {
  const { t, locale } = useI18n();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const orgTz = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters | null>(null);
  const [again, setAgain] = useState<ReportDraft | null>(null);

  const q = useQuery({
    queryKey: [INSIGHTS_KEY, 'report', id],
    queryFn: () => insights.getInsightReport(id),
    refetchInterval: (query) => (isRunning(query.state.data?.data) ? 3000 : false),
    retry: (n, e) => !(isApiError(e) && e.status === 404) && n < 2
  });
  const report = q.data?.data;
  const done = report?.status === 'done';
  const rowsQ = useQuery({
    queryKey: [INSIGHTS_KEY, 'rows', id],
    queryFn: () => insights.listAllInsightRows(id),
    enabled: done,
    staleTime: 5 * 60_000
  });
  const all = useMemo(() => rowsQ.data ?? [], [rowsQ.data]);
  const tz = report?.metrics?.timezone ?? orgTz;
  const f = filters ?? (report ? emptyFilters(report.from, report.to) : null);
  const rows = useMemo(() => (f ? applyFilters(all, f, tz) : []), [all, f, tz]);

  const tabs = TABS.filter((x) => x !== 'questions' || (report?.focus.length ?? 0) > 0);
  const tab: Tab = tabs.find((x) => x === params.get('tab')) ?? 'overview';
  const choose = (next: string) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', next); return n; }, { replace: true });
  const onSee = (refs: string[]) => {
    if (!report) return;
    setFilters({ ...emptyFilters(report.from, report.to), refs });
    choose('chats');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const download = useMutation({
    mutationFn: async (format: 'pdf' | 'xlsx') => ({ format, blob: await insights.downloadInsightReport(id, format) }),
    onSuccess: ({ format, blob }) => downloadBlob(blob, `insights-${report?.from}-${report?.to}.${format}`),
    onError: (e) => notifyError(insightErrorText(t, e), t('insights.report.download'))
  });
  const remove = useMutation({
    mutationFn: () => insights.deleteInsightReport(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [INSIGHTS_KEY] });
      notifySuccess(t('insights.list.deleted'), t('insights.title'));
      navigate('/insights');
    },
    onError: (e) => notifyError(insightErrorText(t, e), t('insights.title'))
  });

  if (q.isPending) return <Stack gap="lg"><Skeleton height={60} /><Skeleton height={320} /></Stack>;
  if (q.error || !report) {
    const missing = isApiError(q.error) && q.error.status === 404;
    return (
      <EmptyState title={t(missing ? 'insights.report.missing' : 'insights.report.loadFailed')}
        description={missing ? t('insights.report.missingHint') : insightErrorText(t, q.error)}
        action={<Link to="/insights"><Button size="sm">{t('insights.report.back')}</Button></Link>} />
    );
  }

  const runAgain = () => setAgain({ from: report.from, to: report.to, channels: report.channels, depth: report.depth, focus: report.focus });
  const description = [
    report.sampled ? t('insights.report.readSampled', { read: formatCount(report.read), chats: formatCount(report.chats) })
      : t('insights.report.read', { read: formatCount(report.read), chats: formatCount(report.chats) }),
    t('insights.depth.models', { reader: report.reader_model, writer: report.writer_model }),
    report.spent_minor > 0 ? t('insights.list.cost', { amount: formatCredits(report.spent_minor, t, report.currency) }) : null
  ].filter(Boolean).join(' · ');
  const props: TabProps | null = f ? { report, rows, all, filters: f, filtered: isFiltered(f, report), tz, onSee } : null;

  return (
    <Stack gap="lg" className="rr-insights">
      <Group gap="xs"><Link to="/insights"><Text role="caption">{t('insights.report.back')}</Text></Link></Group>
      <PageHeader
        title={t('insights.report.title', { period: periodLabel(t, report.from, report.to, locale) })}
        description={description}
        withDivider={false}
        actions={<Group gap="sm" wrap="nowrap"><ReportStatus status={report.status} t={t} /><HelpButton guide="insights" /></Group>}
      />
      {/* The report's actions sit in a row of their own, which wraps on a phone instead of running off its edge. */}
      <Group gap="sm" wrap="wrap" data-testid="insights-report-actions">
        {done && (
          <Button size="sm" variant="default" loading={download.isPending && download.variables === 'pdf'} onClick={() => download.mutate('pdf')}
            data-testid="insights-download-pdf">{t('insights.report.pdf')}</Button>
        )}
        {done && (
          <Button size="sm" variant="default" loading={download.isPending && download.variables === 'xlsx'} onClick={() => download.mutate('xlsx')}
            data-testid="insights-download-xlsx">{t('insights.report.xlsx')}</Button>
        )}
        {!isRunning(report) && <Button size="sm" variant="default" onClick={runAgain} data-testid="insights-run-again">{t('insights.report.again')}</Button>}
        {!isRunning(report) && (
          <Button size="sm" variant="subtle" color="red" loading={remove.isPending} data-testid="insights-report-delete"
            onClick={() => openConfirmModal({
              title: t('insights.list.deleteTitle'),
              children: <Text role="bodySmall">{t('insights.list.deleteBody')}</Text>,
              labels: { confirm: t('insights.list.delete'), cancel: t('action.cancel') },
              onConfirm: () => remove.mutate()
            })}>
            {t('insights.list.delete')}
          </Button>
        )}
      </Group>

      {isRunning(report) && <RunningCard report={report} />}
      {(report.status === 'failed' || report.status === 'cancelled' || report.status === 'skipped') && (
        <Alert color={report.status === 'failed' ? 'error' : 'warning'} data-testid="insights-report-problem">
          <Stack gap="xs">
            <Text role="bodySmall">{report.error ?? t(`insights.report.${report.status}`)}</Text>
            <div><Button size="xs" onClick={runAgain}>{t('insights.report.again')}</Button></div>
          </Stack>
        </Alert>
      )}

      {done && (rowsQ.isPending ? <Skeleton height={320} /> : rowsQ.error ? (
        <Alert color="error" role="alert">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{insightErrorText(t, rowsQ.error)}</span>
            <Button size="xs" variant="default" onClick={() => void rowsQ.refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      ) : props && (
        <>
          {report.sampled && <Text role="caption" c={MUTED}>{t('insights.report.sampledNote', { read: formatCount(report.read), chats: formatCount(report.chats) })}</Text>}
          <FilterBar report={report} rows={all} shown={rows.length} filters={props.filters} onChange={setFilters} t={t} />
          <div data-guide="insights-tabs" className="rr-insights__scroll-x">
            <Tabs value={tab} onChange={choose}>
              <Tabs.List>
                {tabs.map((x) => <Tabs.Tab key={x} value={x}>{t(`insights.tabs.${x}`)}</Tabs.Tab>)}
              </Tabs.List>
            </Tabs>
          </div>
          {tab === 'overview' && <Overview {...props} />}
          {tab === 'topics' && <Topics {...props} />}
          {tab === 'chatbot' && <Chatbot {...props} />}
          {tab === 'stopped' && <Stopped {...props} />}
          {tab === 'gaps' && <Gaps {...props} />}
          {tab === 'demand' && <Demand {...props} />}
          {tab === 'improvements' && <Improvements {...props} />}
          {tab === 'chats' && <Chats {...props} />}
          {tab === 'questions' && <Questions {...props} />}
          <Text role="caption" c={MUTED}>{t('insights.report.depthNote', { depth: depthLabel(t, report.depth) })}</Text>
        </>
      ))}

      <NewReportModal opened={again !== null} onClose={() => setAgain(null)} draft={again}
        onStarted={(r) => navigate(`/insights/${r.id}`)} />
    </Stack>
  );
}
